import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import { SearchAdminProspectPlacesAPI, ImportAdminProspectsAPI } from '../../../../api/AdminAPI';
import { CATEGORY_OPTIONS, SELECT_ABOVE_DIALOG } from '../prospectOptions';
import PlaceResultRow from './PlaceResultRow';

/** Vale a pena importar quem tem por onde chamar */
const hasChannel = (item) => !!(item.whatsapp || item.instagram || item.email);

/**
 * ProspectSearchDialog - Busca no Google Maps (categoria + cidade) e importa os escolhidos.
 * O Instagram e o e-mail vêm do site de cada lugar, quando ele publica.
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onImported recebe (contatos importados, quantos foram pulados)
 * @param {Function} props.onCancel
 */
function ProspectSearchDialog({ isOpen, onImported, onCancel }) {
  const [form, setForm] = useState({ category: 'physio', city: '', term: '' });
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setResults([]);
    setSelected([]);
    setNextToken(null);
    setError('');
  }, [isOpen]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const search = (pageToken) => {
    setSearching(true);
    setError('');
    SearchAdminProspectPlacesAPI({ ...form, page_token: pageToken || undefined }, (response) => {
      setSearching(false);
      if (!response?.success) {
        setError(response?.error || 'Não foi possível buscar.');
        return;
      }
      const items = response.items || [];
      setResults((prev) => (pageToken ? [...prev, ...items] : items));
      setSelected((prev) => [
        ...(pageToken ? prev : []),
        ...items.filter((item) => !item.existing_id && hasChannel(item)).map((item) => item.source_ref),
      ]);
      setNextToken(response.next_page_token || null);
    });
  };

  const toggle = (ref) => setSelected((prev) => (prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]));

  const handleConfirm = () => {
    setImporting(true);
    setError('');
    const items = results.filter((item) => selected.includes(item.source_ref));
    ImportAdminProspectsAPI({ source: 'google_maps', category: form.category, items }, (response) => {
      setImporting(false);
      if (!response?.success) {
        setError(response?.error || 'Não foi possível adicionar.');
        return;
      }
      onImported(response.items || [], response.skipped || 0);
    });
  };

  const busy = searching || importing;
  const withChannel = results.filter(hasChannel).length;

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title="Buscar contatos no Google Maps"
      variant="info"
      confirmLabel={selected.length ? `Adicionar ${selected.length} ao catálogo` : 'Adicionar ao catálogo'}
      cancelLabel="Fechar"
      onConfirm={handleConfirm}
      onCancel={onCancel}
      confirmDisabled={busy || selected.length === 0}
      loading={importing}
      maxWidth={880}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          component="form"
          onSubmit={(e) => { e.preventDefault(); search(); }}
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '170px 1fr 1fr auto' }, gap: 1.5, alignItems: 'center' }}
        >
          <TextField size="small" select label="Categoria" value={form.category} onChange={set('category')} disabled={busy} SelectProps={SELECT_ABOVE_DIALOG}>
            {CATEGORY_OPTIONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Cidade" placeholder="Campinas, SP" value={form.city} onChange={set('city')} disabled={busy} autoFocus />
          <TextField size="small" label="Buscar por (opcional)" placeholder="ex.: fisioterapia pélvica" value={form.term} onChange={set('term')} disabled={busy} />
          <Button type="submit" variant="contained" disabled={busy || form.city.trim() === ''} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
            Buscar
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {results.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {results.length} lugares, {withChannel} com WhatsApp, Instagram ou e-mail. Os marcados entram no catálogo como Novo.
          </Typography>
        )}

        <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {results.map((item) => (
            <PlaceResultRow key={item.source_ref} item={item} checked={selected.includes(item.source_ref)} onToggle={toggle} />
          ))}
          {searching && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, justifyContent: 'center' }}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">Buscando e lendo os sites...</Typography>
            </Box>
          )}
        </Box>

        {nextToken && !searching && (
          <Button onClick={() => search(nextToken)} disabled={busy} sx={{ alignSelf: 'center', textTransform: 'none' }}>
            Mais resultados
          </Button>
        )}
      </Box>
    </ConfirmDialog>
  );
}

export default ProspectSearchDialog;
